import { createApi } from '@reduxjs/toolkit/query/react';
import config from 'config/index';
import httpClient from 'rtk/query/client';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import { EUserType } from 'types/accessControll.types';

const reducerPath = 'outreachCampaigns' as const;
const path = 'v1' as const;

type QuestionCondition = 'required' | 'preferred' | 'optional';
type QuestionType = 'boolean' | 'date' | 'text' | 'number';

export interface OutreachClientSettingsDto {
  clientId: string;
  agentId?: string;
  phoneNumberId?: string;
}

export interface OutreachCampaignLanguageConfigDto {
  language: string;
  role: string;
  pitch: string;
  smsTemplate: string;
  questions: {
    id: string;
    text: string;
    condition: QuestionCondition;
    type: QuestionType;
    expectedAnswer?: boolean | string | number;
  }[];
  endings: {
    negativeExplanation: string;
    negativeEnding: string;
    positiveEnding: string;
  };
}

export interface OutreachCampaignConfigDto {
  timezone: string;
  startDate?: string;
  endDate?: string | null;
  startTime?: string;
  endTime?: string;
  daysActive?: number[];
  enableSms: boolean;
  enableCall: boolean;
  smsToCallDelay?: number;

  maxRetries?: number;
  retryDelay?: number;
}

export interface CreateOutreachCampaignDto {
  clientId: string;
  name: string;
  companyName: string;
  customerName: string;
  outreachInterviewTemplateId?: string;
  communications?: {
    language: string;
    smsTemplate: string;
  }[];
  role?: string;
  languages?: OutreachCampaignLanguageConfigDto[];
  defaultLanguage?: string;
  status: string;
  description?: string;
  callHours?: string;
  period?: string;
  config: OutreachCampaignConfigDto;
  smsTemplate?: string;
  candidates?: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    language: string;
  }[];
  agentId?: string;
}

export interface AgentDto {
  id: string;
  name: string;
  description: string;
  avatar?: string;
}

export interface GetPhoneNumberResponse {
  phone_number_id: string;
  phone_number: string;
  label: string;
  provider: string;
}

export interface OutreachCampaignDto {
  id?: string;
  clientId: string;
  name: string;
  themeId?: string;
  companyName: string;
  customerName: string;
  defaultLanguage: string;
  // languages: OutreachCampaignLanguageConfigDto[];
  createdAt: string;
  status: string;
  description: string;
  stats?: {
    targeted?: number;
    active?: number;
    completed?: number;
    candidatesCompleted?: number;
    qualified?: number;
    averageCallDuration?: number;
    candidatesInvited?: number;
    candidatesPending?: number;
    candidatesQualified?: number;
    candidatesOptOut?: number;
    candidatesPickedUp?: number;
    csatAverageScore?: number;
    csatAnswerRate?: number;
    csatAnswers?: number;
  };
  callHours?: string;
  period?: string;
  config: OutreachCampaignConfigDto;
  role?: string;
  outreachInterviewTemplateId?: string;
  communications?: {
    language: string;
    smsTemplate: string;
  }[];
  activityLog?: {
    type: 'campaign_created' | 'candidates_added';
    createdAt: string | Date;
    metadata?: Record<string, unknown>;
  }[];
  agentId?: string;
}

export interface CloneOutreachCampaignDto {
  targetClientId?: string;
  newCampaignName?: string;
  newTemplateName?: string;
}

export interface CloneOutreachInterviewTemplateDto {
  targetClientId?: string;
  newName?: string;
}

export interface OutreachInterviewTemplateLanguageDto {
  language: string;
  role: string;
  pitch: string;
  questions: {
    id: string;
    text: string;
    condition: QuestionCondition;
    type: QuestionType;
    expectedAnswer?: boolean | string | number;
  }[];
  endings: {
    negativeExplanation: string;
    negativeEnding: string;
    positiveEnding: string;
  };
}

export interface OutreachInterviewTemplateDto {
  id: string;
  name: string;
  clientId: string;
  defaultLanguage: string;
  languages: OutreachInterviewTemplateLanguageDto[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateOutreachInterviewTemplateDto {
  name: string;
  clientId: string;
  defaultLanguage: string;
  languages: OutreachInterviewTemplateLanguageDto[];
}

export interface OutreachCandidateDto {
  id: string;
  name: string;
  phone: string;
  language: string;
  status: string;
  lastContact?: string;
  duration?: string;
}

export const outreachApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  tagTypes: ['Outreach', 'OutreachTemplate'],
  endpoints: (builder) => ({
    getOutreachCampaigns: builder.query<(OutreachCampaignDto & { client?: { name: string } })[], string | void>({
      query: (clientId) => ({
        url: `/outreach/campaigns/find`,
        method: 'POST',
        data: clientId ? { clientId } : {},
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      transformResponse: (response: { data: OutreachCampaignDto[] }) => response.data,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Outreach' as const, id })), { type: 'Outreach', id: 'LIST' }]
          : [{ type: 'Outreach', id: 'LIST' }],
    }),
    getOutreachCampaignById: builder.query<OutreachCampaignDto, string>({
      query: (campaignId) => ({
        url: `/outreach/campaigns/${campaignId}`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      transformResponse: (response: { data: OutreachCampaignDto }) => response.data,
      providesTags: (_result, _error, arg) => [{ type: 'Outreach', id: arg }],
    }),
    getOutreachAgents: builder.query<AgentDto[], void>({
      query: () => ({
        url: `/outreach/campaigns/agents`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      transformResponse: (response: { data: AgentDto[] }) => response.data,
    }),
    getOutreachPhoneNumbers: builder.query<GetPhoneNumberResponse[], void>({
      query: () => ({
        url: `/outreach/campaigns/phone-numbers`, // Mapped to controller method
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      transformResponse: (response: { data: GetPhoneNumberResponse[] }) => response.data,
    }),
    getOutreachcandidatesByClientId: builder.query<OutreachCandidateDto[], string>({
      query: (clientId) => ({
        url: `/outreach/candidates/find`,
        method: 'POST',
        data: { clientId },
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      transformResponse: (response: { data: OutreachCandidateDto[] }) => response.data,
    }),
    createOutreachCampaign: builder.mutation<OutreachCampaignDto, CreateOutreachCampaignDto>({
      query: (data) => ({
        url: `/outreach/campaigns`,
        method: 'POST',
        data,
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      invalidatesTags: [{ type: 'Outreach', id: 'LIST' }],
      transformResponse: (response: { data: OutreachCampaignDto }) => response.data,
    }),
    updateOutreachCampaign: builder.mutation<OutreachCampaignDto, { id: string; data: CreateOutreachCampaignDto }>({
      query: ({ id, data }) => ({
        url: `/outreach/campaigns/${id}`,
        method: 'PATCH',
        data,
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Outreach', id: arg.id },
        { type: 'Outreach', id: 'LIST' },
      ],
      transformResponse: (response: { data: OutreachCampaignDto }) => response.data,
    }),
    getClientSettings: builder.query<OutreachClientSettingsDto | null, string>({
      query: (clientId) => ({
        url: `/outreach/clients/${clientId}/settings`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      transformResponse: (response: { data: OutreachClientSettingsDto | null }) => response.data,
      providesTags: (_result, _error, arg) => [{ type: 'Outreach', id: `SETTINGS-${arg}` }],
    }),
    updateClientSettings: builder.mutation<
      OutreachClientSettingsDto,
      { clientId: string; agentId?: string; phoneNumberId?: string }
    >({
      query: ({ clientId, ...data }) => ({
        url: `/outreach/clients/${clientId}/settings`,
        method: 'PUT',
        data,
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Outreach', id: `SETTINGS-${arg.clientId}` }],
      transformResponse: (response: { data: OutreachClientSettingsDto }) => response.data,
    }),
    findOutreachTemplates: builder.query<OutreachInterviewTemplateDto[], { clientId?: string }>({
      query: (data) => ({
        url: `/outreach/templates/find`,
        method: 'POST',
        data,
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      transformResponse: (response: { data: OutreachInterviewTemplateDto[] }) => response.data,
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'OutreachTemplate' as const, id })),
              { type: 'OutreachTemplate', id: arg.clientId ? `CLIENT-${arg.clientId}` : 'LIST' },
            ]
          : [{ type: 'OutreachTemplate', id: arg.clientId ? `CLIENT-${arg.clientId}` : 'LIST' }],
    }),
    getOutreachTemplateById: builder.query<OutreachInterviewTemplateDto, string>({
      query: (templateId) => ({
        url: `/outreach/templates/${templateId}`,
        method: 'GET',
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      transformResponse: (response: { data: OutreachInterviewTemplateDto }) => response.data,
      providesTags: (_result, _error, arg) => [{ type: 'OutreachTemplate', id: arg }],
    }),
    createOutreachTemplate: builder.mutation<OutreachInterviewTemplateDto, CreateOutreachInterviewTemplateDto>({
      query: (data) => ({
        url: `/outreach/templates`,
        method: 'POST',
        data,
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'OutreachTemplate', id: `CLIENT-${arg.clientId}` }],
      transformResponse: (response: { data: OutreachInterviewTemplateDto }) => response.data,
    }),
    updateOutreachTemplate: builder.mutation<
      OutreachInterviewTemplateDto,
      { id: string; data: Partial<CreateOutreachInterviewTemplateDto> }
    >({
      query: ({ id, data }) => ({
        url: `/outreach/templates/${id}`,
        method: 'PUT',
        data,
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'OutreachTemplate', id: arg.id },
        { type: 'OutreachTemplate', id: 'LIST' },
      ],
      transformResponse: (response: { data: OutreachInterviewTemplateDto }) => response.data,
    }),
    cloneOutreachCampaign: builder.mutation<
      OutreachCampaignDto,
      { campaignId: string; data: CloneOutreachCampaignDto }
    >({
      query: ({ campaignId, data }) => ({
        url: `/outreach/campaigns/${campaignId}/clone`,
        method: 'POST',
        data,
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      invalidatesTags: [{ type: 'Outreach', id: 'LIST' }],
      transformResponse: (response: { data: OutreachCampaignDto }) => response.data,
    }),
    cloneOutreachTemplate: builder.mutation<
      OutreachInterviewTemplateDto,
      { templateId: string; data: CloneOutreachInterviewTemplateDto }
    >({
      query: ({ templateId, data }) => ({
        url: `/outreach/templates/${templateId}/clone`,
        method: 'POST',
        data,
        headers: {
          'X-Access-Control': EUserType.HUBERT,
        },
      }),
      invalidatesTags: [{ type: 'OutreachTemplate', id: 'LIST' }],
      transformResponse: (response: { data: OutreachInterviewTemplateDto }) => response.data,
    }),
  }),
});

// Standalone export function to use httpClient directly but be co-located
export const exportOutreachCampaignCandidates = async (
  campaignId: string,
  filters?: { qualificationStatus?: string; callStatus?: string },
) => {
  const response = await httpClient.post(
    `/v1/outreach/campaigns/candidates/export-csv`,
    { campaignId, ...filters },
    {
      responseType: 'blob',
      headers: {
        'X-Access-Control': EUserType.HUBERT,
      },
    },
  );
  return response.data;
};

export const {
  useGetOutreachCampaignsQuery,
  useGetOutreachCampaignByIdQuery,
  useGetOutreachAgentsQuery,
  useGetOutreachPhoneNumbersQuery,
  useCreateOutreachCampaignMutation,
  useUpdateOutreachCampaignMutation,
  useGetClientSettingsQuery,
  useUpdateClientSettingsMutation,
  useFindOutreachTemplatesQuery,
  useGetOutreachTemplateByIdQuery,
  useCreateOutreachTemplateMutation,
  useUpdateOutreachTemplateMutation,
  useCloneOutreachCampaignMutation,
  useCloneOutreachTemplateMutation,
} = outreachApi;
